// TodayPage — the back-office home screen ("cockpit").
//
// One round-trip to /api/dashboard/today, polled every 10 seconds. Layout is a
// 2-col grid: LEFT column stacks Today (today's installs/estimates/tasks/
// warranty, date-titled, collapsible) + a light-touch Low Inventory glance;
// RIGHT column is Open Quotes ('quoted' + has quote_amount, sorted by age).
//
// Low Inventory here is a cockpit-level glance (links to the Inventory drawer
// for detail); the same rows also feed the topbar bell (NotificationsBell) +
// Awaiting Payment. The low-inventory rows reflect the 14-day commit window
// (worker lib/inventory-commitment.ts).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  Mail,
  Inbox,
  Ruler,
  Wrench,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../components/ui/card.js';
import { Badge } from '../components/ui/badge.js';
import { Skeleton } from '../components/ui/skeleton.js';
import { getDashboardToday, getJobDetail } from '../api/client.js';
import type {
  DashboardTodaysJobRow,
  DashboardTodaysTaskRow,
  DashboardTodaysEstimateRow,
  DashboardTodaysWarrantyRow,
  DashboardOpenQuoteRow,
  DashboardLowInventoryRow,
} from '@gutterinv/shared';
import { TASK_TYPE_LABELS } from '@gutterinv/shared';
import { taskStyle } from '../lib/task-styles.js';
import { cn } from '../lib/utils.js';
import { formatDate, formatTodayHeader } from '../lib/format.js';
import { isPostInstallStatus } from '../lib/calendar-dates.js';
import { useQuoteFormat } from '../lib/useQuoteFormat.js';
import { JobStatusBadge } from '../components/JobStatusBadge.js';
import { EmptyState } from '../components/EmptyState.js';
import { qk } from '../lib/queryKeys.js';
import { isBlockingError } from '../lib/isBlockingError.js';
import { usePollActive } from '../hooks/usePollActive.js';


// 30s to match AppShell/AppShellSidebar, which poll the same ['dashboard','today']
// key at 30s. At 10s this page's interval won (React Query shortest-interval-wins)
// and drove app-wide dashboard polling at 10s. 30s is glanceable-fresh and cuts
// free-tier Supabase egress (2026-06-11 egress-quota mitigation).
const POLL_INTERVAL_MS = 30_000;

export function TodayPage() {
  // Gate polling to office hours (Mon–Fri 07:00–17:00 ET); fetches on mount regardless.
  const pollActive = usePollActive();
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: qk.dashboard.today(),
    queryFn: getDashboardToday,
    refetchInterval: pollActive ? POLL_INTERVAL_MS : false,
  });

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (isBlockingError(error, !!data)) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Failed to load dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : String(error)}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            The server is temporarily unavailable — this usually clears on its own in a moment.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="pt-6 space-y-6">
      {/* Flat 3-card grid: Today (r1c1) + Open Quotes (r1c2) share a row so
          their heights match (grid stretch → both equal the taller); Low
          Inventory auto-flows to r2c1, half-width under Today. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <TodaysJobsPanel
          rows={data.todaysJobs}
          tasks={data.todaysTasks}
          estimates={data.todaysEstimates}
          warranty={data.todaysWarranty}
        />
        <OpenQuotesPanel rows={data.openQuotes} />
        <LowInventoryPanel rows={data.lowInventory} total={data.lowInventoryTotal} />
      </div>
      <p className="text-right text-xs text-muted-foreground">
        Auto-refreshing every 10s · last updated{' '}
        {new Date(dataUpdatedAt).toLocaleTimeString()}
      </p>
    </div>
  );
}

// ============================================================
// TODAY'S JOBS
// ============================================================

function TodaysJobsPanel({
  rows,
  tasks,
  estimates,
  warranty,
}: {
  rows: DashboardTodaysJobRow[];
  tasks: DashboardTodaysTaskRow[];
  estimates: DashboardTodaysEstimateRow[];
  warranty: DashboardTodaysWarrantyRow[];
}) {
  const queryClient = useQueryClient();
  const { formatTotal } = useQuoteFormat();
  const prefetchJob = (jobId: string) => {
    void queryClient.prefetchQuery({ queryKey: qk.jobs.detail(jobId), queryFn: () => getJobDetail(jobId), staleTime: 10_000 });
  };
  // Installs + estimates + tasks + warranty all count toward the card's
  // badge, empty-state, and visibility gate.
  const total = rows.length + tasks.length + estimates.length + warranty.length;
  const [expanded, setExpanded] = useState(true);
  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={expanded}
        >
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">{formatTodayHeader()}</CardTitle>
          {total > 0 && (
            <Badge variant="secondary" className="ml-auto tnum">
              {total}
            </Badge>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              total > 0 ? '' : 'ml-auto',
              expanded ? '' : '-rotate-90',
            )}
          />
        </button>
        {expanded && (
          <CardDescription className="mt-1.5">Work scheduled today.</CardDescription>
        )}
      </CardHeader>
      {expanded && (
      <CardContent>
        {total === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Nothing scheduled"
            description="No jobs, estimates, tasks, or warranty visits are scheduled for today."
          />
        ) : (
          <div className="divide-y divide-border">
            {rows.map((job) => (
              <Link key={job.jobId} to={`/jobs/${job.jobId}`} onMouseEnter={() => prefetchJob(job.jobId)} className="block -mx-3 rounded-md px-3 py-3 transition-colors hover:bg-primary/[0.06] no-underline">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'text-[0.9375rem] font-semibold leading-tight text-foreground',
                        isPostInstallStatus(job.status) && 'line-through opacity-60',
                      )}
                    >
                      {job.displayName}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {job.customerName}
                      <span className="mx-1.5 text-taupe">·</span>
                      {job.address}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <JobStatusBadge status={job.status} />
                    {job.quoteAmountCents !== null && (
                      <div className="tnum text-sm font-semibold text-foreground">
                        {formatTotal(job.quoteAmountCents)}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
            {tasks.map((t) => {
              const st = taskStyle(t.taskType);
              return (
                <Link
                  key={t.taskId}
                  to={`/calendar?date=${t.taskDate}`}
                  className="block -mx-3 rounded-md px-3 py-3 transition-colors hover:bg-primary/[0.06] no-underline"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div
                        className={cn(
                          'flex items-center gap-1.5 text-[0.9375rem] font-semibold leading-tight text-foreground',
                          t.done && 'line-through opacity-60',
                        )}
                      >
                        <st.Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {TASK_TYPE_LABELS[t.taskType]}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {t.peopleNames.join(', ')}
                        {t.notes && (
                          <>
                            <span className="mx-1.5 text-taupe">·</span>
                            {t.notes}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span
                        className={cn(
                          'rounded border px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wider',
                          st.chip,
                        )}
                      >
                        Task
                      </span>
                      {t.done && <span className="text-xs text-muted-foreground">Done</span>}
                    </div>
                  </div>
                </Link>
              );
            })}
            {estimates.map((e) => (
              <Link
                key={e.assignmentId}
                to={`/jobs/${e.jobId}`}
                onMouseEnter={() => prefetchJob(e.jobId)}
                className="block -mx-3 rounded-md px-3 py-3 transition-colors hover:bg-primary/[0.06] no-underline"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'flex items-center gap-1.5 text-[0.9375rem] font-semibold leading-tight text-foreground',
                        e.done && 'line-through opacity-60',
                      )}
                    >
                      <Ruler className="h-4 w-4 shrink-0 text-muted-foreground" />
                      {e.displayName}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {e.customerName}
                      <span className="mx-1.5 text-taupe">·</span>
                      {e.address}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="rounded border border-border px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wider text-muted-foreground">
                      Estimate
                    </span>
                    {e.done && <span className="text-xs text-muted-foreground">Done</span>}
                  </div>
                </div>
              </Link>
            ))}
            {warranty.map((w) => (
              <Link
                key={w.visitId}
                to={`/jobs/${w.jobId}`}
                onMouseEnter={() => prefetchJob(w.jobId)}
                className="block -mx-3 rounded-md px-3 py-3 transition-colors hover:bg-primary/[0.06] no-underline"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        'flex items-center gap-1.5 text-[0.9375rem] font-semibold leading-tight text-foreground',
                        w.done && 'line-through opacity-60',
                      )}
                    >
                      <Wrench className="h-4 w-4 shrink-0 text-muted-foreground" />
                      {w.displayName}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {w.customerName}
                      {w.peopleNames.length > 0 && (
                        <>
                          <span className="mx-1.5 text-taupe">·</span>
                          {w.peopleNames.join(', ')}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="rounded border border-border px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wider text-muted-foreground">
                      Warranty
                    </span>
                    {w.done && <span className="text-xs text-muted-foreground">Done</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
}

// ============================================================
// OPEN QUOTES
// ============================================================

function OpenQuotesPanel({ rows }: { rows: DashboardOpenQuoteRow[] }) {
  const [expanded, setExpanded] = useState(true);
  const { formatTotal } = useQuoteFormat();

  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={expanded}
        >
          <Mail className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">Open Quotes</CardTitle>
          <Badge
            variant={rows.length > 0 ? 'secondary' : 'outline'}
            className="ml-auto tnum"
          >
            {rows.length}
          </Badge>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              expanded ? '' : '-rotate-90',
            )}
          />
        </button>
        {expanded && (
          <CardDescription className="mt-1.5">
            Quotes sent but not yet accepted — oldest first
          </CardDescription>
        )}
      </CardHeader>
      {expanded && (
        <CardContent>
          {rows.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No open quotes"
              description="Every quote has been accepted, lost, or has no quote yet."
            />
          ) : (
            <div className="divide-y divide-border">
              {rows.map((q) => (
                <Link key={q.jobId} to={`/jobs/${q.jobId}`} className="block -mx-3 rounded-md px-3 py-3 transition-colors hover:bg-primary/[0.06] no-underline">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[0.9375rem] font-semibold leading-tight text-foreground">
                        {q.displayName}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {q.customerName}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="tnum text-base font-bold text-foreground">
                        {formatTotal(q.quoteAmountCents)}
                      </div>
                      <div className="mt-0.5 text-[0.6875rem] uppercase tracking-wider text-taupe">
                        {q.quoteSentAt ? `Sent ${formatDate(q.quoteSentAt)}` : 'Not sent'}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ============================================================
// LOW INVENTORY
// ============================================================

// Light-touch low-stock glance for the cockpit — a half-width card under
// Today, mirroring the topbar bell's Low Inventory section. Rows link to the
// SKU drawer on the Inventory page (the full detail lives there). The
// underlying `lowInventory` data already reflects the 14-day commit window
// (worker lib/inventory-commitment.ts), so a SKU only appears once a job's
// materials actually commit against it.
function LowInventoryPanel({
  rows,
  total,
}: {
  rows: DashboardLowInventoryRow[];
  /** Real total — `rows` is capped to the top-N by deficit for egress. */
  total: number;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={expanded}
        >
          <AlertTriangle className="h-5 w-5 text-warning" />
          <CardTitle className="text-base">Low Inventory</CardTitle>
          <Badge
            variant={total > 0 ? 'secondary' : 'outline'}
            className="ml-auto tnum"
          >
            {total}
          </Badge>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              expanded ? '' : '-rotate-90',
            )}
          />
        </button>
      </CardHeader>
      {expanded && (
        <CardContent>
          {rows.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">
              All stocked — nothing at or below its reorder point.
            </p>
          ) : (
            // -mx-6 negates CardContent's px-6 so the list + scrollbar span the
            // card edges (bell-style). max-h-[29rem] ≈ 8 two-line rows (~3.6rem
            // each) — tuned, retune if the row height changes; grows to fit ≤8
            // then scrolls internally.
            <>
              <div className="-mx-6 max-h-[29rem] divide-y divide-border overflow-y-auto">
                {rows.map((row) => (
                  <LowInventoryRow key={row.skuId} row={row} />
                ))}
              </div>
              {total > rows.length && (
                <p className="mt-2 text-xs text-taupe">
                  Showing top {rows.length} of {total} — full list on the Inventory page.
                </p>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function LowInventoryRow({ row }: { row: DashboardLowInventoryRow }) {
  return (
    <Link
      to={`/inventory?detail=${row.skuId}`}
      className="flex items-center justify-between gap-3 px-6 py-2.5 transition-colors hover:bg-primary/[0.06] no-underline"
    >
      {/* min-w-0 flex-1 + truncate absorbs the ~15px the vertical scrollbar
          steals so the nowrap right-side numbers never force an h-scrollbar. */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">
          {row.name}
          {row.variant && (
            <span className="font-normal text-muted-foreground"> · {row.variant}</span>
          )}
        </div>
        <div className="mt-0.5">
          <span className="ticker">{row.code}</span>
        </div>
      </div>
      <div className="flex items-baseline gap-2 whitespace-nowrap text-right">
        <span
          className={cn(
            'tnum text-sm font-bold',
            row.effectivelyFree < 0 ? 'text-destructive' : 'text-warning',
          )}
        >
          {row.effectivelyFree.toLocaleString()}/{row.reorderThreshold.toLocaleString()} {row.unit}
        </span>
        <span className="tnum text-xs text-destructive">−{row.deficit.toLocaleString()}</span>
      </div>
    </Link>
  );
}

// ============================================================
// SKELETON
// ============================================================

function DashboardSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Skeleton className="h-96" />
      <Skeleton className="h-96" />
    </div>
  );
}
